import { Box, Collapse, HStack, Heading, useDisclosure } from '@chakra-ui/react'
import { CSSProperties, ReactNode, useRef } from 'react'

import { ChevronIcon } from '../../components/Icons/ChevronIcon'
import { createWidget } from '../widget-utils'

export interface CollapsableSectionWidgetProps {
  caption?: string
  subCaption?: string
  subCaptionTooltip?: ReactNode
  children?: ReactNode
  accordionExpanded?: boolean
  collapseContentStyle?: CSSProperties
  /**
   * true if padding should be removed between content and parent.
   */
  fillContent?: boolean
}

export const CollapsableSectionWidget = ({
  caption,
  subCaption,
  subCaptionTooltip,
  children,
  accordionExpanded = true,
  fillContent = false,
  collapseContentStyle = {},
  ...props
}: CollapsableSectionWidgetProps) => {
  const elementRef = useRef<HTMLDivElement>(null)
  const { isOpen, onToggle } = useDisclosure({
    defaultIsOpen: accordionExpanded,
  })

  const handleCollapseEnd = () => {
    if (isOpen) {
      elementRef.current?.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }

  return (
    <Box as="section" {...props}>
      <HStack as="button" onClick={onToggle} spacing="3">
        <Heading as="h2" fontSize="21px" fontWeight="500">
          {caption}
        </Heading>
        <ChevronIcon
          transition="transform 150ms ease"
          boxSize="9.6px"
          color="presets.neutral.80"
          h="4.8px"
          transform={`rotate(${isOpen ? '180deg' : '0deg'})`}
        />

        {subCaption && (
          <Box fontSize="12px" fontWeight="500" alignSelf="flex-end">
            {subCaption}
          </Box>
        )}
        {subCaptionTooltip}
      </HStack>
      <Collapse
        in={isOpen}
        // Fixes display:none bug. Some chilren need to be able to measure dimensions.
        // May need to visually hide this, or use unmountonexit in the future.
        // If unmountonexit is used, need to ensure network calls are at page-root.
        startingHeight={1}
        onAnimationComplete={handleCollapseEnd}
      >
        <Box
          style={collapseContentStyle}
          pt={fillContent ? undefined : '25px'}
          ref={elementRef}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  )
}

export const CollapsableSectionWidgetDefinition = createWidget(
  CollapsableSectionWidget,
  {
    type: 'CollapsableSection',
    slots: ['subCaptionTooltip'],
  },
)
